/* eslint-disable react/prop-types */
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { DateTime } from 'luxon';
import tinycolor from 'tinycolor2';
import { css } from 'glamor';
import { library } from '@fortawesome/fontawesome-svg-core';
import { faSquare } from '@fortawesome/free-solid-svg-icons/faSquare';
import { faCheckSquare } from '@fortawesome/free-solid-svg-icons/faCheckSquare';
import { faCircle } from '@fortawesome/free-solid-svg-icons/faCircle';
import { faDotCircle } from '@fortawesome/free-solid-svg-icons/faDotCircle';
import { faTable } from '@fortawesome/free-solid-svg-icons/faTable';
import { faClock } from '@fortawesome/free-solid-svg-icons/faClock';
import { faFilter } from '@fortawesome/free-solid-svg-icons/faFilter';
import { faTimes } from '@fortawesome/free-solid-svg-icons/faTimes';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classNames from 'classnames';

import ApiHelper from './helpers/ApiHelper';
import StyleHelper from '../shared/helpers/StyleHelper';
import SlotsTimetable from './components/SlotsTimetable';
import SlotList from './components/SlotList';
import FacilityTiles from './components/FacilityTiles';
import ActivityFilter from './components/ActivityFilter';
import LocationFilter from './components/LocationFilter';
import { accumulateStylePropertiesFromCustomStyleSections } from '../shared/utils/styleUtils';

import '../Timetable.css';

library.add(faSquare);
library.add(faCheckSquare);
library.add(faCircle);
library.add(faDotCircle);
library.add(faTable);
library.add(faClock);
library.add(faFilter);
library.add(faTimes);

class Timetable extends Component {
  constructor(props) {
    super(props);
    this.state = {
      date: DateTime.local().toISODate(),
      facets: {},
      filter: this.defaultFilter(this.props.activity),
      location: this.props.location || this.props.initialLocation,
      facilities: { item: [] },
      selectedFacility: null,
      slots: [],
      isLoading: true,
      listOverride: false,
      showFilters: false,
    };
    this.calendarMaxWidth = 650;
    this.resizeTimer = null;
    this.resizeFunction = null;
    this.height = null;
    if (typeof this.props.height === 'string') {
      let test = this.props.height.replace('px', '');
      if (test.match(/^[0-9]+$/)) {
        // convert to rem
        this.height = this.pxToRem(test);
      } else if (test.match(/^[0-9.]+rem$/)) {
        this.height = parseFloat(test.replace('rem', ''));
      }
    } else if (typeof this.props.height === 'number') {
      this.height = this.pxToRem(this.props.height);
    }
    this.getFacets();
    this.getFacilities(true);
    this.updateSelectedFacility = this.updateSelectedFacility.bind(this);
  }

  pxToRem(value) {
    return (
      parseInt(value, 10) /
      parseInt(window.getComputedStyle(document.body.parentNode).fontSize.replace('px', ''), 10)
    );
  }

  defaultFilter(activity) {
    return {
      activity: activity ? [activity] : [],
      timeOfDay: 'all',
    };
  }

  componentDidUpdate() {
    if (typeof this.props.didUpdate === 'function') {
      this.props.didUpdate();
    }
  }

  updateSelectedFacility(facilityUse) {
    if (facilityUse) {
      this.getSlots(facilityUse);
    } else {
      this.setState({
        ...this.state,
        selectedFacility: facilityUse,
        slots: [],
      });
    }
  }

  handleResize() {
    const node = this.timetable;
    if (node && typeof node.getBoundingClientRect === 'function') {
      if (node.getBoundingClientRect().width <= this.calendarMaxWidth) {
        this.setState({
          listOverride: true,
          tilesPerRow: 2,
        });
      } else {
        this.setState({
          listOverride: false,
          tilesPerRow: 3,
        });
      }
    }
  }

  componentDidMount() {
    this.resizeFunction = () => {
      clearTimeout(this.resizeTimer);
      this.resizeTimer = setTimeout(() => {
        this.handleResize();
      }, 250);
    };
    window.addEventListener('resize', this.resizeFunction);
    this.handleResize();
  }

  componentWillUnmount() {
    clearTimeout(this.resizeTimer);
    window.removeEventListener('resize', this.resizeFunction);
  }

  toggleShowFilters() {
    this.setState({
      showFilters: !this.state.showFilters,
    });
  }

  onDateChange(date) {
    this.setState(
      {
        date,
      },
      () => {
        this.getFacilities();
      }
    );
  }

  onLocationChange(location) {
    if (!location) {
      this.setState({
        isLoading: false,
      });
    } else {
      this.setState(
        {
          location,
        },
        () => {
          this.getFacilities();
        }
      );
    }
  }

  onActivityChange(activity) {
    const filter = Object.assign({}, this.state.filter);
    filter.activity = activity;
    this.setState({
      filter,
    });
  }

  onTimeOfDayChange(timeOfDay) {
    const filter = Object.assign({}, this.state.filter);
    filter.timeOfDay = timeOfDay;
    this.setState({
      filter,
    });
  }

  /**
   * @param  {...string} customStyleSectionKeys
   */
  getBrandStyle(...customStyleSectionKeys) {
    const brand =
      accumulateStylePropertiesFromCustomStyleSections(
        this.props.customStyle,
        customStyleSectionKeys
      ) ?? {};
    if (typeof brand.backgroundColor !== 'undefined') {
      const background = tinycolor(brand.backgroundColor);
      if (background.isValid()) {
        // Set a default text color from the background color
        if (typeof brand.color === 'undefined') {
          brand.color = getDefaultTextColorFromBackgroundColor(background);
        }
        // If no background hover color is defined, make it a lighter version of the background color.
        if (typeof brand[':hover'] === 'undefined') {
          brand[':hover'] = {};
        }
        if (typeof brand[':hover'].backgroundColor === 'undefined') {
          brand[':hover'].backgroundColor = background.lighten().toString();
        }
      }
    }
    if (
      typeof brand[':hover'] !== 'undefined' &&
      typeof brand[':hover'].backgroundColor !== 'undefined' &&
      typeof brand[':hover'].color === 'undefined'
    ) {
      // Default the hover text color from the background color
      const hoverBackground = tinycolor(brand[':hover'].backgroundColor);
      if (hoverBackground.isValid()) {
        brand[':hover'].color = getDefaultTextColorFromBackgroundColor(hoverBackground);
      }
    }
    return brand;
  }

  getFacets() {
    ApiHelper.facets(this.props.apiKey, this.props.provider, this.props.cache).then((facets) => {
      this.setState({ ...this.state, facets });
    });
  }

  getFacilities(firstRun) {
    if (this.state.location && this.state.date) {
      const doGetFacilities = () => {
        ApiHelper.facilities(
          this.props.apiKey,
          this.props.location || this.state.location,
          this.props.provider,
          this.props.activity,
          this.props.iminTag,
          this.props.iminTagNot
        ).then((res) => {
          this.setState({ isLoading: false, facilities: res });
        });
      };
      if (firstRun) {
        doGetFacilities();
      } else {
        this.setState({ isLoading: true }, doGetFacilities);
      }
    }
  }

  getSlots(facilityUse) {
    if (this.state.location && this.state.date) {
      const doGetSlots = () => {
        ApiHelper.facility(this.props.apiKey, facilityUse.identifier).then((res) => {
          this.setState({
            isLoading: false,
            selectedFacility: res,
            slots: res.event || [],
          });
        });
      };
      this.setState({ isLoading: true }, doGetSlots);
    }
  }

  loadFonts() {
    if (typeof this.props.fonts !== 'undefined' && this.props.fonts instanceof Array) {
      for (const font of this.props.fonts) {
        if (typeof font.cssSrc !== 'undefined') {
          css.insert(`@import url(${font.cssSrc});`);
        } else {
          css.fontFace(font);
        }
      }
    }
  }

  render() {
    this.loadFonts();
    const filterStyle = this.getBrandStyle('filter');
    const baseStyle = this.getBrandStyle('base');
    const baseWhitelist = ['fontFamily'];
    if (typeof filterStyle.fontFamily === 'undefined') {
      if (typeof baseStyle.fontFamily === 'undefined') {
        filterStyle.fontFamily = baseStyle.fontFamily;
      }
    }
    css.global(
      '.Timetable .Filter button',
      StyleHelper.applyWhitelistToStyle(filterStyle, baseWhitelist)
    );

    let locationFilter;
    if (!this.props.location) {
      locationFilter = (
        <LocationFilter
          location={this.state.location}
          locations={this.state.facets.locations}
          apiKey={this.props.apiKey}
          provider={this.props.provider}
          onChange={(location) => this.onLocationChange(location)}
          cache={this.props.cache}
        />
      );
    }

    let activityFilter;
    if (!this.props.activity) {
      activityFilter = (
        <ActivityFilter
          facilitiesSearchResponse={this.state.facilities}
          filter={this.state.filter.activity}
          onChange={(activity) => this.onActivityChange(activity)}
          isCategoryFilter={this.props.isCategoryFilter}
        />
      );
    }

    return (
      <div
        className={classNames({
          Timetable: true,
          FacilitiesTimetable: true,
          presetLocation: !!this.props.location,
          filterLocation: !this.props.location,
          embedded: !!this.height,
        })}
        style={StyleHelper.applyWhitelistToStyle(baseStyle, baseWhitelist)}
        ref={(timetable) => (this.timetable = timetable)}
      >
        {this.state.selectedFacility ? (
          this.state.listOverride ? (
            <div className="filters selected-facility">
              <div className="header">
                <button onClick={() => this.updateSelectedFacility(null)}>
                  Back to facilities
                </button>
                <div className="header-item header-location-item">
                  {this.state.selectedFacility.location.name}
                </div>
              </div>
              <div className="header header-centred">
                <div className="header-item">{this.state.selectedFacility.name}</div>
              </div>
            </div>
          ) : (
            <div className="filters selected-facility header">
              <button onClick={() => this.updateSelectedFacility(null)}>Back to facilities</button>
              <div className="header-item">{this.state.selectedFacility.name}</div>
              <div className="header-item header-location-item">
                {this.state.selectedFacility.location.name}
              </div>
            </div>
          )
        ) : (
          <div className="filters">
            <div className="smallMenu">
              <button onClick={() => this.toggleShowFilters()}>
                <FontAwesomeIcon icon="filter" /> Filter
              </button>
            </div>
            <div className={`subFilters ${this.state.showFilters ? 'showFilters' : ''}`}>
              {locationFilter}
              {activityFilter}
            </div>
          </div>
        )}
        {this.state.isLoading ? (
          <div className="Loading">
            <span>Loading...</span>
          </div>
        ) : this.state.facilities &&
          this.state.facilities['imin:item'] &&
          this.state.facilities['imin:item'].length > 0 ? (
          this.state.selectedFacility ? (
            this.state.listOverride ? (
              <SlotList
                date={this.state.date}
                height={this.height}
                buttonStyle={this.getBrandStyle('button', 'bookButton')}
                memberBookButtonStyle={this.getBrandStyle('memberBookButton')}
                hourStyle={this.getBrandStyle('button', 'hourButton')}
                customStyle={this.getBrandStyle('timetable')}
                filter={this.state.filter}
                facility={this.state.selectedFacility}
                slots={this.state.slots}
                checkoutUrl={this.props.checkoutUrl}
                baseMemberBookUrl={this.props.baseMemberBookUrl}
                checkoutTheme={this.props.checkoutTheme}
                didUpdate={this.props.didUpdate}
                updateSelectedFacility={this.updateSelectedFacility}
                bookableProviders={this.props.bookableProviders}
                shouldShowBookAsGuestButton={this.props.shouldShowBookAsGuestButton}
                shouldShowBookAsMemberButton={this.props.shouldShowBookAsMemberButton}
                memberDeepLinks={this.props.memberDeepLinks}
                shouldShowNoAvailability={this.props.shouldShowNoAvailability}
                showPrices={this.props.showPrices}
                numberOfDaysLookahead={
                  this.props.numberOfDaysLookahead || this.props.defaultDaysLookahead
                }
              />
            ) : (
              <SlotsTimetable
                date={this.state.date}
                height={this.height}
                buttonStyle={this.getBrandStyle('button', 'bookButton')}
                memberBookButtonStyle={this.getBrandStyle('memberBookButton')}
                hourStyle={this.getBrandStyle('button', 'hourButton')}
                customStyle={this.getBrandStyle('timetable')}
                unavailableTimetableSlotStyle={this.getBrandStyle('unavailableTimetableSlot')}
                filter={this.state.filter}
                facility={this.state.selectedFacility}
                slots={this.state.slots}
                checkoutUrl={this.props.checkoutUrl}
                baseMemberBookUrl={this.props.baseMemberBookUrl}
                checkoutTheme={this.props.checkoutTheme}
                didUpdate={this.props.didUpdate}
                updateSelectedFacility={this.updateSelectedFacility}
                bookableProviders={this.props.bookableProviders}
                shouldShowBookAsGuestButton={this.props.shouldShowBookAsGuestButton}
                shouldShowBookAsMemberButton={this.props.shouldShowBookAsMemberButton}
                memberDeepLinks={this.props.memberDeepLinks}
                shouldShowNoAvailability={this.props.shouldShowNoAvailability}
                showPrices={this.props.showPrices}
                numberOfDaysLookahead={
                  this.props.numberOfDaysLookahead || this.props.defaultDaysLookahead
                }
              />
            )
          ) : (
            <FacilityTiles
              date={this.state.date}
              height={this.height}
              buttonStyle={this.getBrandStyle('button', 'bookButton')}
              hourStyle={this.getBrandStyle('button', 'hourButton')}
              customStyle={this.getBrandStyle('timetable')}
              filter={this.state.filter}
              facilities={this.state.facilities}
              tilesPerRow={this.state.tilesPerRow}
              checkoutUrl={this.props.checkoutUrl}
              checkoutTheme={this.props.checkoutTheme}
              didUpdate={this.props.didUpdate}
              updateSelectedFacility={this.updateSelectedFacility}
              bookableProviders={this.props.bookableProviders}
              isCategoryFilter={this.props.isCategoryFilter}
              showPrices={this.props.showPrices}
            />
          )
        ) : this.state.location ? (
          <div className="NoResults">
            <span>No facilities could be found</span>
          </div>
        ) : (
          <div className="NoResults">
            <span>Please select a location to see results</span>
          </div>
        )}
      </div>
    );
  }
}

Timetable.defaultProps = {
  customStyle: {},
  cache: true,
  defaultDaysLookahead: 7,
  fonts: [],
  height: null,
  initialLocation: null,
  location: null,
  showPrices: true,
};

Timetable.propTypes = {
  showPrices: PropTypes.bool,
  defaultDaysLookahead: PropTypes.number,
  numberOfDaysLookahead: PropTypes.number,
};

/**
 * @param {tinycolor.Instance} backgroundColor
 */
function getDefaultTextColorFromBackgroundColor(backgroundColor) {
  if (backgroundColor.isLight()) {
    return '#000';
  } else {
    return '#fff';
  }
}

export default Timetable;
